// Import / export of a single logbook to a flat, human-readable JSON file.
//
// The on-disk shape (see serializeLogbook) hoists every type-specific value out
// of the entry's `fields` JSON blob so the file reads naturally and can be
// hand-edited. `flattenEntry` / `splitEntryFields` in LogbookTypes.mjs are an
// exact inverse pair, so export and import are pure reuse of that machinery.
//
// Import always creates a BRAND-NEW logbook on the target item -- it never
// touches existing data -- so anything that only makes sense inside the
// original item/logbook (foreign keys) or is server-assigned (id, timestamps)
// is deliberately stripped on the way out and refused on the way in.
//
// The one exception to "never touches existing data" is the gazetteer, which is
// global rather than per-item: the file carries a `places` section (PlaceIO.mjs)
// holding the TaskPogoPlace rows its routes refer to, and the importer creates
// the ones the target environment is missing. Without it a route's `placeId`
// names a row in the environment that wrote the file, and the binding is
// dropped on the first read. The section is additive, so LOGBOOK_EXPORT_VERSION
// stays 1: an older importer ignores it and loses a binding, never a position.

import {
  LOGBOOK_TYPES,
  LOGBOOK_TYPE_MAINTENANCE,
  fieldsForType,
  flattenEntry,
  splitEntryFields,
} from './LogbookTypes.mjs';
import {
  ENTRY_ROUTE_KEY,
  routeFromEntry,
  routeFromFields,
  mergeRouteIntoFields,
  resolveRouteAgainstPlaces,
} from './TripRoute.mjs';
import { placesForExport, placesForImport } from './PlaceIO.mjs';

// The entry's geo route, hoisted to the top level of the file like every other
// json-backed value. It is NOT a registry field (see splitEntryFields), so it
// has to be named here on both sides -- otherwise a route would survive
// everywhere except an export/import round trip, which is the one place a user
// would reasonably expect their whole logbook to come back.
const ROUTE_EXPORT_KEY = ENTRY_ROUTE_KEY;

export const LOGBOOK_EXPORT_FORMAT = 'taskpogo.logbook';
export const LOGBOOK_EXPORT_VERSION = 1;

// Keys that must never round-trip through an export file: server-assigned
// metadata and foreign keys that only make sense within the original
// item/logbook. `itemCounterId` and `taskId` in particular would dangle in any
// other item, so they are dropped even though `itemCounterId` is a declared
// Maintenance field.
const NON_EXPORTED_KEYS = new Set([
  'id',
  'owner',
  'createdAt',
  'updatedAt',
  '__typename',
  'itemId',
  'logbookId',
  'taskId',
  'itemCounterId',
]);

// The set of entry keys a given type legitimately carries: its declared field
// keys (and any interval `monthsKey`), minus the never-exported keys above.
// Used to whitelist a (possibly hand-edited) import file so it can't inject
// arbitrary attributes into the create mutation.
function allowedKeysForType(typeId) {
  const keys = new Set();
  for (const field of fieldsForType(typeId)) {
    if (!NON_EXPORTED_KEYS.has(field.key)) keys.add(field.key);
    if (field.monthsKey && !NON_EXPORTED_KEYS.has(field.monthsKey)) {
      keys.add(field.monthsKey);
    }
  }
  // Not a declared field, so it has to be allowed explicitly.
  keys.add(ROUTE_EXPORT_KEY);
  return keys;
}

// Build the export envelope for a logbook and its (already-loaded) entries.
// `now` is injectable for deterministic tests.
//
// `placeIndex` is loadPlaceIndex()'s { byId, byKey }. Given one, the file also
// carries the gazetteer rows its routes refer to, so importing it into another
// environment can recreate them -- without it a waypoint's `placeId` names a
// row that only exists where the file was written. Omitting it produces a
// byte-identical file to before the section existed, which is also what an
// export does when the gazetteer can't be read: an export must never fail
// because the place list is unreachable.
export function serializeLogbook(logbook, entries, { now = new Date(), placeIndex } = {}) {
  const typeId = logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;
  const routes = [];
  const exportedEntries = (entries || []).map((entry) => {
    const flat = flattenEntry(entry, typeId);
    const clean = {};
    for (const [key, value] of Object.entries(flat)) {
      if (NON_EXPORTED_KEYS.has(key)) continue;
      if (value === null || value === undefined) continue;
      clean[key] = value;
    }
    // flattenEntry drops the blob, and the route isn't a registry field, so
    // it has to be lifted out separately. Read through routeFromEntry rather
    // than the raw blob so the file always holds a normalized route.
    const route = routeFromEntry(entry);
    if (route) {
      clean[ROUTE_EXPORT_KEY] = route;
      routes.push(route);
    }
    return clean;
  });

  const places = placesForExport(routes, placeIndex);

  return {
    format: LOGBOOK_EXPORT_FORMAT,
    version: LOGBOOK_EXPORT_VERSION,
    exportedAt: now.toISOString(),
    logbook: { name: logbook?.name ?? '', logbookType: typeId },
    // Only when there is something to say. The key's absence is what keeps a
    // no-index export identical to a pre-section one.
    ...(places.length > 0 ? { places } : {}),
    entries: exportedEntries,
  };
}

// Parse + validate raw file text into a trusted { logbook, entries } shape.
// Throws an Error with a human-readable message on any problem so callers can
// route it straight to showError.
export function parseLogbookExport(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('This file is not valid JSON.');
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('This file is not a TaskPogo logbook export.');
  }
  if (data.format !== LOGBOOK_EXPORT_FORMAT) {
    throw new Error('This file is not a TaskPogo logbook export.');
  }
  if (data.version !== LOGBOOK_EXPORT_VERSION) {
    throw new Error(`Unsupported logbook export version: ${data.version}.`);
  }

  const logbook = data.logbook;
  if (!logbook || typeof logbook !== 'object' || Array.isArray(logbook)) {
    throw new Error('This logbook export is missing its logbook details.');
  }

  const typeId = logbook.logbookType;
  // logbookTypeById() silently falls back to Maintenance for unknown ids, which
  // is right for rendering legacy rows but wrong here -- an unknown type in an
  // import file is a real error, so check membership explicitly.
  if (!LOGBOOK_TYPES.some((t) => t.id === typeId)) {
    throw new Error(`Unknown logbook type: ${typeId}.`);
  }

  if (!Array.isArray(data.entries)) {
    throw new Error('This logbook export has no entries list.');
  }
  data.entries.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      throw new Error(`Entry ${index + 1} is not a valid object.`);
    }
    if (entry.date === null || entry.date === undefined || entry.date === '') {
      throw new Error(`Entry ${index + 1} is missing a date.`);
    }
  });

  // The place rows to ensure exist before the entries are created: the file's
  // own section, plus a minimal pin for any name its routes refer to that the
  // section doesn't cover. Read through the same tolerant reader
  // entriesToCreateInputs uses, and never mutating `entries`.
  //
  // A missing or malformed `places` section is NOT a parse error -- the entries
  // are the user's data and the places are a bonus, so a junk section must not
  // make an otherwise good file un-importable.
  const routes = data.entries
    .map((entry) => routeFromFields({ [ENTRY_ROUTE_KEY]: entry[ROUTE_EXPORT_KEY] }, ENTRY_ROUTE_KEY))
    .filter(Boolean);

  return {
    logbook: {
      name: typeof logbook.name === 'string' ? logbook.name : '',
      logbookType: typeId,
    },
    places: placesForImport(data.places, routes),
    entries: data.entries,
  };
}

// Turn parsed entries into create-mutation inputs for a specific target
// item/logbook. Whitelists to the type's known keys, then rebuilds the `fields`
// JSON blob via splitEntryFields (Database.withoutNullIndexKeys serializes that
// blob to an AWSJSON string when the mutation runs).
//
// `placeIndex` is the TARGET environment's gazetteer, after the importer has
// created whatever was missing. Every route is bound against it here, before
// the row is written, so the stored blob names rows that exist rather than the
// source environment's ids. Without one this is exactly what it always was --
// resolveRouteAgainstPlaces returns the same route for an absent or empty
// index, so the routes land unbound and rebind on their first read.
export function entriesToCreateInputs(entries, typeId, { itemId, logbookId, placeIndex }) {
  const allowed = allowedKeysForType(typeId);
  return (entries || []).map((entry) => {
    const flat = {};
    for (const [key, value] of Object.entries(entry)) {
      if (allowed.has(key)) flat[key] = value;
    }
    // The route is not a registry field, so splitEntryFields would leave it at
    // the top level of the create input -- an attribute the model doesn't
    // have. Pull it out first and merge it back into the rebuilt blob.
    //
    // It goes back through routeFromFields, not straight in: an import file is
    // hand-editable, and that is what drops malformed waypoints and refuses an
    // unknown version. This module must not parse a route itself.
    const rawRoute = flat[ROUTE_EXPORT_KEY];
    delete flat[ROUTE_EXPORT_KEY];

    const split = splitEntryFields(flat, typeId);
    const parsed = routeFromFields({ [ENTRY_ROUTE_KEY]: rawRoute }, ENTRY_ROUTE_KEY);
    const route = resolveRouteAgainstPlaces(parsed, placeIndex);
    if (route) {
      split.fields = mergeRouteIntoFields(split.fields, route, ENTRY_ROUTE_KEY);
    }
    return { ...split, itemId, logbookId };
  });
}

// Pick a logbook name that doesn't collide with the item's existing logbooks.
// "Trips" -> "Trips (imported)" -> "Trips (imported 2)" -> ...
export function uniqueLogbookName(desired, existingNames) {
  const taken = new Set(existingNames || []);
  const base = (desired && desired.trim()) || 'Imported Logbook';
  if (!taken.has(base)) return base;
  const first = `${base} (imported)`;
  if (!taken.has(first)) return first;
  let n = 2;
  while (taken.has(`${base} (imported ${n})`)) n += 1;
  return `${base} (imported ${n})`;
}

// A friendly download filename, e.g. "trips-2026-08-02.json".
export function suggestedFileName(logbook, now = new Date()) {
  const slug =
    (logbook?.name || 'logbook')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'logbook';
  const date = now.toISOString().slice(0, 10);
  return `${slug}-${date}.json`;
}
